/**
 * Ficha de uma embarcação.
 *
 * O desenho segue uma regra: **o que falta é dito, não escondido**. Metade da
 * frota amazônica transmite Classe A sem preencher o estático (medido: a
 * BERTOLINI VI não declara destino, dimensões nem IMO). Deixar campos vazios
 * faria parecer defeito do portal; a ficha diz, com todas as letras, que o
 * transponder daquela embarcação não declara aquilo.
 *
 * E o destino é sempre "declarado": é o que a embarcação DIZ, digitado à mão
 * na ponte, não o que ela faz.
 */
import { useEffect, useMemo } from 'react';
import { Anchor, Navigation, TriangleAlert } from 'lucide-react';
import { formatLonLat } from '../lib/format';
import { formatDateTimeBoth, formatTimeBrt } from '../lib/time';
import { destinoDe } from './destinos';
import { etaObsoleta, resolverEta } from './eta';
import { calado as fmtCalado, dimensoes, idade, IDADE_AVISO_MS, rumo, velocidade } from './format';
import { tipoDe } from './kinds';
import { COR_FAMILIA, situacaoDe } from './look';
import { bandeiraDe, emojiBandeira, especieDe, NOME_ESPECIE } from './mmsi';
import { useShipStore } from './store';
import { distanciaKm } from './track';
import type { NavioFeature } from './layer';

/** Linha "rótulo · valor" da ficha. */
function Linha(props: { rotulo: string; children: React.ReactNode; nota?: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-[11px] text-ink-400">{props.rotulo}</span>
      <span className="text-right text-xs text-ink-100">
        {props.children}
        {props.nota && <span className="block text-[10px] text-ink-400">{props.nota}</span>}
      </span>
    </div>
  );
}

/** Mini-percurso das últimas 24 h, na escala da própria embarcação. */
function MiniPercurso({ trilha }: { trilha: NavioFeature['navio']['trilha'] }) {
  const km = distanciaKm(trilha);
  const caminho = useMemo(() => {
    if (trilha.length < 2) return null;
    const lats = trilha.map((a) => a[1]);
    const lons = trilha.map((a) => a[2]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const dLat = Math.max(1e-4, maxLat - minLat);
    const dLon = Math.max(1e-4, maxLon - minLon);
    // Mantém a proporção: um percurso reto não pode virar zigue-zague.
    const escala = Math.max(dLat, dLon);
    return trilha
      .map((a, i) => {
        const x = 50 + ((a[2] - (minLon + maxLon) / 2) / escala) * 90;
        const y = 16 - ((a[1] - (minLat + maxLat) / 2) / escala) * 28;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }, [trilha]);

  if (!caminho || km < 0.5) return null;

  return (
    <div className="pt-1">
      <svg viewBox="0 0 100 32" width="100%" height={34} role="img" aria-label={`Percurso de ${km.toFixed(0)} km nas últimas 24 horas`}>
        <path d={caminho} fill="none" stroke="rgba(244,211,94,0.85)" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
        <circle
          cx={caminho.split(' ').pop()!.slice(1).split(',')[0]}
          cy={caminho.split(' ').pop()!.slice(1).split(',')[1]}
          r="2"
          fill="#f4d35e"
        />
      </svg>
      <div className="text-[10px] text-ink-400">{km.toFixed(0)} km nas últimas 24 h</div>
    </div>
  );
}

export function ShipPopup({ dado }: { dado: NavioFeature }) {
  const { navio, familia } = dado;
  const ficha = navio.ficha;
  const selecionar = useShipStore((s) => s.selecionar);

  // Abrir a ficha seleciona: é o que faz a trilha aparecer no mapa.
  useEffect(() => {
    selecionar(navio.mmsi);
    return () => selecionar(null);
  }, [navio.mmsi, selecionar]);

  const agora = Date.now();
  // "Ouvida" é sobre a EMBARCAÇÃO: a última vez que a fonte a recebeu. O
  // instante do quadro é outra coisa — a posição da trilha que está sendo
  // mostrada — e misturar os dois fazia um navio ouvido há cinco minutos
  // aparecer como "sem contato há três horas" só porque o loop estava atrás.
  const quando = navio.visto;
  const velha = agora - quando > IDADE_AVISO_MS;
  const noQuadro = dado.instante !== null && Math.abs(dado.instante - navio.visto) > 5 * 60_000 ? dado.instante : null;

  const tipo = tipoDe(ficha?.tipo ?? null);
  const bandeira = bandeiraDe(navio.mmsi);
  const especie = especieDe(navio.mmsi);
  const destino = destinoDe(ficha?.destinoCru);
  const etaMs = resolverEta(ficha?.eta ? paraEtaCrua(ficha.eta) : null, agora);
  const etaPassou = etaMs !== null && etaMs < agora;
  const dim = dimensoes(ficha?.comprimento ?? null, ficha?.boca ?? null);
  const situacao = situacaoDe(navio.navStatus);

  const parado = navio.sog === null || navio.sog < 0.5;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-ink-400">
        <span className="inline-block size-2 rounded-[2px]" style={{ background: COR_FAMILIA[familia] }} aria-hidden />
        {especie === 'embarcacao' ? `${tipo.rotulo} · AIS` : `${NOME_ESPECIE[especie]} · AIS`}
      </div>

      <div className="leading-tight font-semibold text-white">
        {ficha?.nome ?? `MMSI ${navio.mmsi}`}
        <div className="flex items-center gap-1 text-xs font-normal text-ink-300">
          {bandeira ? (
            <>
              <span aria-hidden>{emojiBandeira(bandeira.iso)}</span>
              {bandeira.pais}
              <span className="text-ink-500">· registro (MID)</span>
            </>
          ) : (
            <span className="text-ink-400">registro não identificado</span>
          )}
        </div>
      </div>

      {/* Posição velha vira aviso no TOPO, não nota de rodapé. */}
      {velha && (
        <div className="flex items-start gap-1.5 rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2 py-1.5 text-[11px] leading-snug text-alerta-200">
          <TriangleAlert size={13} className="mt-px shrink-0" />
          <span>
            Última vez ouvida {idade(quando, agora)}. Esta é a última posição conhecida, não onde a embarcação está agora.
          </span>
        </div>
      )}

      <div className="flex items-baseline gap-1.5 pt-0.5">
        {parado ? (
          <>
            <Anchor size={15} className="shrink-0 self-center text-ink-300" aria-hidden />
            <span className="text-sm text-ink-200">{situacao ?? 'parada'}</span>
          </>
        ) : (
          <>
            <Navigation size={15} className="shrink-0 self-center" style={{ color: COR_FAMILIA[familia] }} aria-hidden />
            <span className="text-sm text-ink-100">
              {velocidade(navio.sog)}
              {rumo(navio.cog) && <span className="text-ink-300"> · {rumo(navio.cog)}</span>}
            </span>
          </>
        )}
      </div>

      <div className="space-y-1 border-t border-white/10 pt-1.5">
        {destino ? (
          <Linha
            rotulo="Destino declarado"
            nota={
              ficha?.declaradoEm && agora - ficha.declaradoEm > 48 * 3600_000
                ? `declarado ${idade(ficha.declaradoEm, agora)}`
                : null
            }
          >
            {destino.rotulo}
            {destino.codigo && <span className="text-ink-400"> ({destino.codigo})</span>}
            {destino.origem && <span className="block text-[10px] text-ink-400">saiu de {destino.origem}</span>}
          </Linha>
        ) : null}

        {etaMs !== null && !etaObsoleta(etaMs, agora) && (
          <Linha rotulo={etaPassou ? 'Chegada informada' : 'Chegada prevista'}>
            {formatDateTimeBoth(etaMs)}
            {etaPassou && <span className="block text-[10px] text-ink-400">já passou</span>}
          </Linha>
        )}

        {dim && <Linha rotulo="Tamanho">{dim}</Linha>}
        {fmtCalado(ficha?.calado ?? null) && <Linha rotulo="Calado">{fmtCalado(ficha!.calado)}</Linha>}
        {situacao && !parado && <Linha rotulo="Situação">{situacao}</Linha>}
      </div>

      <MiniPercurso trilha={navio.trilha} />

      {/*
        O que o transponder não declara é dito, e não deixado em branco: quem
        olha uma ficha pela metade acha que o portal falhou.
      */}
      {!ficha?.nome || (!destino && !dim) ? (
        <div className="rounded-lg bg-white/5 px-2 py-1.5 text-[10px] leading-snug text-ink-400">
          O transponder desta embarcação não declara
          {[!ficha?.nome && 'nome', !destino && 'destino', !dim && 'dimensões', !ficha?.imo && 'IMO']
            .filter(Boolean)
            .join(', ')
            .replace(/, ([^,]*)$/, ' nem $1')}
          . É comum na frota regional, que transmite só a posição.
        </div>
      ) : null}

      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">
        MMSI {navio.mmsi}
        {ficha?.imo ? ` · IMO ${ficha.imo}` : ''}
        {ficha?.indicativo ? ` · indicativo ${ficha.indicativo}` : ''}
        <div>
          {formatLonLat(navio.lon, navio.lat)} · ouvida {idade(quando, agora)} ({formatTimeBrt(quando)})
        </div>
        {noQuadro !== null && <div>posição mostrada: a das {formatTimeBrt(noQuadro)}, no quadro exibido</div>}
        {navio.fonte && <div className="text-ink-500">via {navio.fonte}</div>}
      </div>
    </div>
  );
}

function paraEtaCrua(e: { mes: number; dia: number; hora: number; min: number }) {
  return { Month: e.mes, Day: e.dia, Hour: e.hora, Minute: e.min };
}
